<template>
  <div class="rb-block" :class="{ 'rb-block-on': selected }">
    <div class="rb-block-head">
      <v-icon x-small class="mr-1">{{ icon }}</v-icon>
      <span class="text-truncate">{{ section.title || typeText }}</span>
      <v-spacer />
      <span class="rb-block-w">{{ section.width || 12 }}/12</span>
    </div>

    <!-- tiles -->
    <div v-if="section.type === 'kpi'" class="rb-tiles">
      <div v-for="(it, i) in section.items || []" :key="i" class="rb-tile">
        <div class="rb-tile-v">{{ kpi(it) }}</div>
        <div class="rb-tile-l">{{ it.label }}</div>
      </div>
      <div v-if="!(section.items || []).length" class="rb-empty">No tiles yet</div>
    </div>

    <!-- table -->
    <div v-else-if="section.type === 'table'" class="rb-table">
      <div class="rb-tr rb-th">
        <span v-for="c in columns" :key="c.field" :style="{ textAlign: c.align || 'left' }">{{ c.label }}</span>
      </div>
      <div v-for="(r, i) in rows" :key="i" class="rb-tr">
        <span v-for="c in columns" :key="c.field" :class="{ 'rb-link': c.link }" :style="{ textAlign: c.align || 'left' }">{{ cell(r, c) }}</span>
      </div>
      <div v-if="section.showTotals" class="rb-tr rb-tot"><span>Total</span></div>
      <div v-if="!columns.length" class="rb-empty">Every column of "{{ section.dataset }}" is shown</div>
    </div>

    <!-- chart -->
    <div v-else-if="section.type === 'chart'" class="rb-chart">
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" width="100%" height="64">
        <template v-if="section.chart === 'pie'">
          <circle cx="50" cy="20" r="16" fill="none" stroke="#1976d2" stroke-width="10" stroke-dasharray="45 100" />
          <circle cx="50" cy="20" r="16" fill="none" stroke="#66bb6a" stroke-width="10" stroke-dasharray="30 100" stroke-dashoffset="-45" />
          <circle cx="50" cy="20" r="16" fill="none" stroke="#ffb300" stroke-width="10" stroke-dasharray="25 100" stroke-dashoffset="-75" />
        </template>
        <template v-else-if="section.chart === 'line' || section.chart === 'area'">
          <polyline :points="line" fill="none" stroke="#1976d2" stroke-width="1.5" />
          <polygon v-if="section.chart === 'area'" :points="`0,40 ${line} 100,40`" fill="#1976d2" opacity="0.25" />
        </template>
        <template v-else>
          <rect v-for="(h, i) in bars" :key="i" :x="i * (100 / bars.length) + 1.5" :y="40 - h" :width="100 / bars.length - 3" :height="h" fill="#1976d2" />
        </template>
      </svg>
      <div class="rb-tile-l">{{ section.chart || 'bar' }} chart of {{ (section.series || []).map((s) => s.label || s.field).join(', ') || '...' }} by {{ section.x || '...' }}</div>
    </div>

    <!-- text -->
    <div v-else-if="section.type === 'text'" class="rb-text">{{ section.content || 'Text...' }}</div>
  </div>
</template>

<script>
import { SECTION_TYPES } from './editorState'
import { formatValue, aggregate } from '../rbUtil'

// A drawing of one section for the layout canvas. It shows real figures from the last sample run when
// there is one, and neutral placeholders otherwise.
export default {
  name: 'RbSectionBlock',
  props: {
    section: { type: Object, required: true },
    selected: Boolean,
    datasets: { type: Object, default: null } // result.datasets of a sample run
  },
  computed: {
    typeText () { return (SECTION_TYPES.find((t) => t.value === this.section.type) || {}).text },
    icon () { return (SECTION_TYPES.find((t) => t.value === this.section.type) || {}).icon },
    ds () { return (this.datasets && this.datasets[this.section.dataset]) || null },
    columns () {
      if (this.section.columns && this.section.columns.length) return this.section.columns.filter((c) => !c.hidden).slice(0, 8)
      return ((this.ds && this.ds.columns) || []).slice(0, 8).map((f) => ({ field: f, label: f }))
    },
    rows () { return this.ds ? this.ds.rows.slice(0, 3) : [{}, {}, {}] },
    bars () {
      if (this.ds && this.section.series && this.section.series[0]) {
        const vals = this.ds.rows.slice(0, 12).map((r) => Number(r[this.section.series[0].field]) || 0)
        const max = Math.max(...vals, 1)
        if (vals.length) return vals.map((v) => Math.max(2, (v / max) * 36))
      }
      return [14, 24, 18, 32, 26, 36, 20]
    },
    line () {
      const n = this.bars.length
      return this.bars.map((h, i) => `${(i / Math.max(n - 1, 1)) * 100},${40 - h}`).join(' ')
    }
  },
  methods: {
    kpi (it) {
      const d = this.datasets && this.datasets[it.dataset]
      if (!d) return it.format === 'currency' ? '$0.00' : '123'
      return formatValue(aggregate(d.rows, it.field, it.aggregate), it.format || 'number')
    },
    cell (r, c) {
      if (!this.ds) return '......'
      return formatValue(r[c.field], c.format)
    }
  }
}
</script>

<style scoped>
.rb-block { border: 1px solid rgba(128, 128, 128, 0.5); border-radius: 6px; background: #fff; padding: 6px 8px; height: 100%; overflow: hidden; position: relative; }
.rb-block-on { border-color: #1976d2; box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.35); }
.rb-block-head { display: flex; align-items: center; font-size: 11px; font-weight: 600; color: #555; margin-bottom: 4px; }
.rb-block-w { font-weight: 400; color: #999; }
.rb-tiles { display: flex; gap: 6px; flex-wrap: wrap; }
.rb-tile { border: 1px solid #ddd; border-radius: 4px; padding: 4px 8px; min-width: 70px; }
.rb-tile-v { font-size: 14px; font-weight: 700; color: #222; }
.rb-tile-l { font-size: 10px; color: #777; }
.rb-table { font-size: 10px; color: #333; }
.rb-tr { display: flex; gap: 6px; border-bottom: 1px solid #eee; padding: 2px 0; }
.rb-tr > span { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rb-th { font-weight: 700; background: #f5f5f5; }
.rb-tot { font-weight: 700; }
.rb-link { text-decoration: underline dotted; }
.rb-empty { font-size: 10px; color: #999; padding: 4px 0; }
.rb-text { font-size: 11px; color: #444; white-space: pre-line; max-height: 70px; overflow: hidden; }
</style>
